import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { suppliersApi } from './api';
import type {
  CreateSupplierPayload,
  UpdateSupplierPayload,
  Supplier,
  SupplierListParams,
  SupplierListResponse,
  SupplierStats,
  SupplierPOListResponse,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useSuppliers(params: SupplierListParams): UseQueryResult<SupplierListResponse> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'suppliers',
      tenantId,
      params.page ?? 1,
      params.search ?? '',
      params.is_active !== undefined ? String(params.is_active) : 'all',
    ],
    queryFn: () => suppliersApi.list(params),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useSupplier(id: string | undefined): UseQueryResult<Supplier | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['suppliers', tenantId, 'detail', id],
    queryFn: () => (id ? suppliersApi.getById(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useSupplierStats(): UseQueryResult<SupplierStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['suppliers', tenantId, 'stats'],
    queryFn: () => suppliersApi.getStats(),
    staleTime: 30_000,
  });
}

export function useSupplierPurchaseOrders(
  supplierId: string | undefined,
  page = 1,
  limit = 10,
): UseQueryResult<SupplierPOListResponse> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['suppliers', tenantId, 'po-history', supplierId, page],
    queryFn: () =>
      supplierId
        ? suppliersApi.listPurchaseOrders(supplierId, page, limit)
        : Promise.resolve({ data: [], meta: { page: 1, limit: 10, total: 0, totalPages: 0 } }),
    enabled: Boolean(supplierId),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useCreateSupplier() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSupplierPayload) => suppliersApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers', tenantId] });
    },
  });
}

export function useUpdateSupplier() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSupplierPayload }) =>
      suppliersApi.update(id, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['suppliers', tenantId] });
      qc.invalidateQueries({ queryKey: ['suppliers', tenantId, 'detail', vars.id] });
    },
  });
}

export function useDeactivateSupplier() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      suppliersApi.deactivate(id, reason),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['suppliers', tenantId] });
      qc.invalidateQueries({ queryKey: ['suppliers', tenantId, 'detail', vars.id] });
    },
  });
}

export function useReactivateSupplier() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => suppliersApi.reactivate(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['suppliers', tenantId] });
      qc.invalidateQueries({ queryKey: ['suppliers', tenantId, 'detail', id] });
    },
  });
}
